/**
 * Your year of check-ins, in one card.
 *
 * Five walks make a membership year: one now, one every three months, and a
 * final one to close the year. This card shows where you are, when the next
 * one opens, and — once the year is done — what comes next.
 */
import { memo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarClock, Check, Lock, Mail, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import {
  MAX_ASSESSMENT_TAKES,
  fetchAssessmentSchedule,
  formatDate,
  scheduleHeadline,
  daysUntil,
  type ScheduleSlot,
} from "@/lib/assessmentSchedule";

function SlotRow({ slot }: { slot: ScheduleSlot }) {
  const done = slot.status === "completed";
  const open = slot.status === "available" || slot.status === "in_progress";
  const Icon = done ? Check : open ? Sparkles : Lock;
  return (
    <li className="flex items-start gap-3">
      <span
        className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full border ${
          done
            ? "border-primary/30 bg-primary/12 text-primary"
            : open
              ? "border-ai/30 bg-ai/12 text-ai"
              : "border-border bg-muted text-muted-foreground"
        }`}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{slot.label}</span>
        <span className="block text-xs text-muted-foreground">
          {done
            ? "Done"
            : slot.status === "in_progress"
              ? "In progress — your answers are saved"
              : slot.status === "available"
                ? "Open now"
                : slot.unlocksAt
                  ? `Opens ${formatDate(slot.unlocksAt)}`
                  : "Opens after your first walk"}
        </span>
      </span>
    </li>
  );
}

function AssessmentScheduleCard() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["assessment-schedule", user?.id ?? "anon"],
    enabled: Boolean(user?.id),
    staleTime: 5 * 60 * 1000,
    queryFn: fetchAssessmentSchedule,
  });

  if (!user) return null;
  if (isLoading) return <Skeleton className="h-56 w-full rounded-3xl" />;
  if (!data || !data.slots.length) return null;

  const next = data.next;
  const days = daysUntil(next?.unlocksAt ?? null);
  const yearEndsIn = daysUntil(data.cycleEndsAt);

  return (
    <section className="calm-card p-6 sm:p-8" aria-labelledby="checkin-schedule-heading">
      <div className="flex items-center gap-2 text-muted-foreground">
        <CalendarClock className="h-4 w-4" aria-hidden="true" />
        <h3
          id="checkin-schedule-heading"
          className="font-display text-sm font-semibold uppercase tracking-[0.14em]"
        >
          Your year of check-ins
        </h3>
      </div>

      <p className="font-display mt-4 text-lg font-semibold leading-tight tracking-tight">
        {scheduleHeadline(data)}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {data.completed} of {MAX_ASSESSMENT_TAKES} done. You grow across the year, so we check in
        every three months — and once more to close it out.
      </p>

      <ul className="mt-5 space-y-3">
        {data.slots.map((s) => (
          <SlotRow key={s.key} slot={s} />
        ))}
      </ul>

      {next && (next.status === "available" || next.status === "in_progress") ? (
        <Button asChild size="lg" className="mt-6 h-12 w-full rounded-2xl text-base sm:w-auto sm:px-8">
          <Link to={next.status === "in_progress" ? "/assessment/v2" : "/start"}>
            {next.status === "in_progress" ? "Pick up where you left off" : "Start this check-in"}
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      ) : data.cycleComplete ? (
        <div className="mt-6 rounded-2xl border border-ai/25 bg-ai/8 p-4">
          <p className="text-sm font-medium">A full year of growth, tracked.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Extend your membership to keep your check-ins, matches and roadmap moving into next
            year.
          </p>
          <Button asChild className="mt-4 h-12 w-full rounded-2xl sm:w-auto sm:px-8">
            <Link to="/payment">
              Continue for another year
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      ) : (
        <p className="mt-6 flex items-start gap-2 text-sm text-muted-foreground">
          <Mail className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Nothing to remember — we'll email you the day it opens
            {days ? ` (in ${days} ${days === 1 ? "day" : "days"})` : ""}.
          </span>
        </p>
      )}

      {!data.cycleComplete && yearEndsIn !== null && yearEndsIn <= 45 && (
        <p className="mt-4 text-sm text-muted-foreground">
          Your year wraps up on {formatDate(data.cycleEndsAt)}.{" "}
          <Link to="/payment" className="underline underline-offset-4 hover:text-foreground">
            Extend your membership
          </Link>{" "}
          to keep going.
        </p>
      )}
    </section>
  );
}

export default memo(AssessmentScheduleCard);
